import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  searchInvestigation,
  SearchRequest,
  PersonSummary,
  SearchResult,
} from '../services/investigationApi';

function InvestigationSearchPage() {
  const navigate = useNavigate();

  // Search form state
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [hotelId, setHotelId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchMode, setSearchMode] = useState<'exact' | 'fuzzy' | 'similar'>('exact');
  const [fuzzyThreshold, setFuzzyThreshold] = useState(0.3);

  // Results state
  const [results, setResults] = useState<PersonSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e?: FormEvent, overridePage?: number) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);

    const currentPage = overridePage ?? page;

    const req: SearchRequest = {
      search_mode: searchMode,
      page: currentPage,
      page_size: pageSize,
    };

    if (phone) req.phone_number = phone;
    if (fullName) req.full_name = fullName;
    if (firstName) req.first_name = firstName;
    if (lastName) req.last_name = lastName;
    if (documentNumber) req.document_number = documentNumber;
    if (nationality) req.nationality = nationality;
    if (dateOfBirth) req.date_of_birth = dateOfBirth;
    if (hotelId) req.hotel_id = hotelId;
    if (dateFrom) req.date_from = dateFrom;
    if (dateTo) req.date_to = dateTo;
    if (searchMode === 'fuzzy') req.fuzzy_threshold = fuzzyThreshold;

    try {
      const data: SearchResult = await searchInvestigation(req);
      setResults(data.results || []);
      setTotalCount(data.total_count);
      setSearched(true);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la recherche');
      setResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const goToPage = (p: number) => {
    setPage(p);
    handleSearch(undefined, p);
  };

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">🔍 Recherche Investigation</h1>
        <p className="text-gray-600">Recherche multi-critères sur la base des fiches de police</p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="bg-white rounded-lg shadow p-6 mb-6">
        {/* Search Mode */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Mode de recherche</label>
          <div className="flex items-center gap-4">
            {(['exact', 'fuzzy', 'similar'] as const).map((mode) => (
              <label key={mode} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="searchMode"
                  value={mode}
                  checked={searchMode === mode}
                  onChange={() => setSearchMode(mode)}
                  className="text-blue-600"
                />
                <span className="text-sm capitalize">{mode}</span>
              </label>
            ))}
          </div>
          {searchMode === 'fuzzy' && (
            <div className="mt-3 flex items-center gap-3">
              <label className="text-sm text-gray-600">Seuil fuzzy: {fuzzyThreshold.toFixed(2)}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={fuzzyThreshold}
                onChange={(e) => setFuzzyThreshold(parseFloat(e.target.value))}
                className="w-48"
                aria-label="Seuil fuzzy"
              />
            </div>
          )}
        </div>

        {/* Search Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label htmlFor="inv-phone" className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <input id="inv-phone" type="text" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+243..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="inv-fullname" className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
            <input id="inv-fullname" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
              placeholder="Nom complet" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="inv-firstname" className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
            <input id="inv-firstname" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
              placeholder="Prénom" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="inv-lastname" className="block text-sm font-medium text-gray-700 mb-1">Nom de famille</label>
            <input id="inv-lastname" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
              placeholder="Nom de famille" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="inv-docnum" className="block text-sm font-medium text-gray-700 mb-1">N° Document</label>
            <input id="inv-docnum" type="text" value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)}
              placeholder="AB123456" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="inv-nationality" className="block text-sm font-medium text-gray-700 mb-1">Nationalité</label>
            <input id="inv-nationality" type="text" value={nationality} onChange={(e) => setNationality(e.target.value)}
              placeholder="CD, FR, US..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="inv-dob" className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
            <input id="inv-dob" type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="inv-hotel" className="block text-sm font-medium text-gray-700 mb-1">ID Hôtel</label>
            <input id="inv-hotel" type="text" value={hotelId} onChange={(e) => setHotelId(e.target.value)}
              placeholder="UUID hôtel" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="inv-datefrom" className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
            <input id="inv-datefrom" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label htmlFor="inv-dateto" className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
            <input id="inv-dateto" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
        </div>

        <button type="submit" disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
          {loading ? 'Recherche...' : 'Rechercher'}
        </button>
      </form>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">{error}</div>
      )}

      {/* Results */}
      {searched && !loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-200 flex justify-between items-center">
            <span className="text-sm text-gray-600">{totalCount} résultat(s) trouvé(s)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom complet</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nationalité</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type document</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Séjours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernier séjour</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alertes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      Aucun résultat trouvé. Essayez d'élargir vos critères.
                    </td>
                  </tr>
                ) : (
                  results.map((person) => (
                    <tr key={person.guest_id} className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/investigation/persons/${person.guest_id}`)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{person.full_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{person.nationality}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{person.document_type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{person.total_stays}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {person.last_stay_date ? new Date(person.last_stay_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {person.active_alerts > 0 ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                            {person.active_alerts}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/investigation/persons/${person.guest_id}`); }}
                          className="text-blue-600 hover:text-blue-900">Voir dossier</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <button onClick={() => goToPage(page - 1)} disabled={page <= 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 hover:bg-gray-50">Précédent</button>
              <span className="text-sm text-gray-600">Page {page} / {totalPages}</span>
              <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 hover:bg-gray-50">Suivant</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default InvestigationSearchPage;
